import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TenantsSkeleton } from './PageSkeletons';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Users, CheckCircle, XCircle, Clock, Phone, Mail, Home, Plus, Edit2, Trash2, Search, User, Sparkles, Loader2, ShieldAlert } from 'lucide-react';
import { DateChip, MultiChip, SingleChip, FilterButton, FiltersSheet, dateChipLabel } from './_filterChips';
import { toast } from '../../hooks/use-toast';
import AddTenantModal from './AddTenantModal';
import EditTenantModal from './EditTenantModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';
import { useEmptyStateTracker } from '../../hooks/useEmptyStateTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TenantsList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, notice, exited
  const [kycFilter, setKycFilter] = useState('all'); // all, verified, pending
  const [searchQuery, setSearchQuery] = useState('');
  const [checkInRange, setCheckInRange] = useState({ preset: 'all', start: '', end: '' });
  const [propertyFilter, setPropertyFilter] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  // Auto-open AddTenantModal once after onboarding wizard
  useEffect(() => {
    if (localStorage.getItem('rk_onboarding_jump_to_tenant') === '1') {
      localStorage.removeItem('rk_onboarding_jump_to_tenant');
      // Delay so layout settles
      setTimeout(() => setShowAddModal(true), 400);
    }
  }, []);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [riskMap, setRiskMap] = useState({});   // tenant_id -> { risk_score, action, label }
  const [riskLoading, setRiskLoading] = useState(null); // tenant_id being scored
  const [hasRooms, setHasRooms] = useState(true);

  const fetchTenants = useCallback(async () => {
    try {
      let url = `${API}/tenants`;
      const params = [];
      
      if (filter !== 'all') params.push(`status=${filter}`);
      if (kycFilter !== 'all') params.push(`kyc_status=${kycFilter}`);
      if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
      
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, kycFilter, searchQuery, token]);

  // Check if user has at least one room before allowing tenant creation
  const checkRoomsExist = useCallback(async () => {
    try {
      const propsRes = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (propsRes.data.length === 0) { setHasRooms(false); return; }
      for (const p of propsRes.data) {
        const rRes = await axios.get(`${API}/properties/${p.id}/rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (rRes.data.length > 0) { setHasRooms(true); return; }
      }
      setHasRooms(false);
    } catch {
      setHasRooms(true); // fail-safe
    }
  }, [token]);

  useEffect(() => { checkRoomsExist(); }, [checkRoomsExist, tenants.length]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Auto-refresh whenever payments or tenants change anywhere in the app
  useDataSync([EVT.TENANT, EVT.PAYMENT], fetchTenants);
  useEmptyStateTracker('tenants', tenants.length, loading);

  const updateKYCStatus = async (tenantId, status) => {
    try {
      await axios.patch(`${API}/tenants/${tenantId}/kyc?kyc_status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      emit(EVT.TENANT, { action: 'kyc_updated', id: tenantId, status });

      toast({
        title: "KYC Updated",
        description: `KYC status updated to ${status}`
      });
      
      fetchTenants();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update KYC status",
        variant: "destructive"
      });
    }
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setShowEditModal(true);
  };

  const handleDeleteTenant = (tenant) => {
    setSelectedTenant(tenant);
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const confirmDeleteTenant = async () => {
    if (!selectedTenant) return;

    setDeleteLoading(true);
    setDeleteError('');

    try {
      await axios.delete(`${API}/tenants/${selectedTenant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      emit(EVT.TENANT, { action: 'deleted', id: selectedTenant.id });
      emit(EVT.ROOM,   { action: 'tenant_deleted', room_id: selectedTenant.room_id });

      toast({
        title: "Tenant Deleted",
        description: "Tenant has been deleted successfully"
      });

      setShowDeleteModal(false);
      setSelectedTenant(null);
      fetchTenants();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete tenant";
      setDeleteError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setDeleteLoading(false);
    }
  };


  const toggleSelectTenant = (tenantId) => {
    setSelectedTenants(prev =>
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTenants.length === tenants.length) {
      setSelectedTenants([]);
    } else {
      setSelectedTenants(tenants.map(t => t.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTenants.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedTenants.length} tenant(s)? This will also delete their payments and update room occupancy.`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/tenants/bulk-delete`,
        selectedTenants,
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        }
      );

      toast({
        title: "Bulk Delete Complete",
        description: `Deleted ${response.data.deleted_count} out of ${response.data.total} tenants`,
      });

      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Bulk delete errors:', response.data.errors);
      }

      setSelectedTenants([]);
      fetchTenants();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete tenants",
        variant: "destructive"
      });
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedTenants.length === 0) return;

    try {
      const response = await axios.patch(
        `${API}/tenants/bulk-update-status`,
        { tenant_ids: selectedTenants, new_status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "Status Updated",
        description: `Updated ${response.data.updated_count} tenant(s) to ${newStatus}`,
      });

      setSelectedTenants([]);
      fetchTenants();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update tenant status",
        variant: "destructive"
      });
    }
  };


  const predictPaymentRisk = async (tenant) => {
    setRiskLoading(tenant.id);
    try {
      const res = await axios.post(
        `${API}/ai/predict/payment-risk/${tenant.id}`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRiskMap(prev => ({ ...prev, [tenant.id]: res.data }));
      toast({
        title: `Risk: ${(res.data.risk_label || 'unknown').toUpperCase()} (${res.data.risk_score}/100)`,
        description: res.data.recommended_action || 'AI risk prediction complete',
      });
    } catch (e) {
      toast({ title: 'AI Risk prediction failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setRiskLoading(null); }
  };

  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.status === 'active').length,
    kycVerified: tenants.filter(t => t.kyc_status === 'verified').length,
    kycPending: tenants.filter(t => t.kyc_status === 'pending').length
  };

  if (loading) {
    return <TenantsSkeleton />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tenants</h1>
          <p className="text-gray-600 mt-1">Manage your tenants and their details</p>
        </div>
        {hasRooms ? (
          <Button
            data-testid="add-tenant-btn"
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
          >
            <Plus size={20} />
            Add Tenant
          </Button>
        ) : (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <span className="text-xs text-amber-800">Add a property & room first to onboard tenants</span>
            <Button
              data-testid="tenants-add-property-cta"
              size="sm"
              variant="outline"
              onClick={() => window.location.href = '/manager/property/list'}
              className="border-amber-400 text-amber-700"
            >
              <Plus size={14} className="mr-1" /> Add Property
            </Button>
          </div>
        )}
      </div>

      {/* Add Tenant Modal */}
      <AddTenantModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={fetchTenants}
        token={token}
      />

      {/* Edit Tenant Modal */}
      <EditTenantModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTenant(null);
        }}
        onSuccess={fetchTenants}
        token={token}
        tenant={selectedTenant}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTenant(null);
          setDeleteError('');
        }}
        onConfirm={confirmDeleteTenant}
        title="Delete Tenant"
        message={
          deleteError || 
          "Are you sure you want to delete this tenant? This will remove all their records."
        }
        itemName={selectedTenant ? selectedTenant.name : ''}
        loading={deleteLoading}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6 border-l-4 border-l-blue-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Tenants</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <Users className="text-blue-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-green-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Active</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.active}</p>
            </div>
            <CheckCircle className="text-green-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-purple-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">KYC Verified</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.kycVerified}</p>
            </div>
            <CheckCircle className="text-purple-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-orange-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">KYC Pending</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.kycPending}</p>
            </div>
            <Clock className="text-orange-600" size={32} />
          </div>
        </Card>
      </div>

      {/* Search + chip strip */}
      <Card className="p-4 border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input
            type="text"
            placeholder="Search by name, phone, or email…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="tenants-search"
          />
        </div>
        <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
          <SingleChip
            testId="chip-tenant-status"
            label={
              filter === 'all' ? 'All Statuses' :
              filter === 'active' ? 'Active' :
              filter === 'notice' ? 'Under Notice' :
              filter === 'exited' ? 'Exited' : 'Status'
            }
            options={[
              { key: 'all',    label: 'All Statuses' },
              { key: 'active', label: 'Active' },
              { key: 'notice', label: 'Under Notice' },
              { key: 'exited', label: 'Exited' },
            ]}
            value={filter}
            onChange={setFilter}
            highlight={filter !== 'all'}
          />
          <SingleChip
            testId="chip-tenant-kyc"
            label={
              kycFilter === 'all' ? 'All KYC' :
              kycFilter === 'verified' ? 'KYC: Verified' :
              kycFilter === 'pending' ? 'KYC: Pending' : 'KYC'
            }
            options={[
              { key: 'all',      label: 'All KYC' },
              { key: 'verified', label: 'Verified' },
              { key: 'pending',  label: 'Pending' },
            ]}
            value={kycFilter}
            onChange={setKycFilter}
            highlight={kycFilter !== 'all'}
          />
          <DateChip
            testId="chip-tenant-checkin"
            value={checkInRange}
            onChange={setCheckInRange}
            label={dateChipLabel(checkInRange, 'Check-in Date')}
            highlight={checkInRange.preset !== 'all' && checkInRange.preset !== ''}
          />
          <FilterButton
            testId="open-tenant-filters"
            onClick={() => setSheetOpen(true)}
            count={
              (filter !== 'all' ? 1 : 0) +
              (kycFilter !== 'all' ? 1 : 0) +
              (checkInRange.preset !== 'all' && checkInRange.preset ? 1 : 0) +
              (propertyFilter.length ? 1 : 0)
            }
          />
        </div>
      </Card>

      {/* Bulk Actions */}
      {selectedTenants.length > 0 && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-blue-900">
              {selectedTenants.length} tenant(s) selected
            </p>
            <div className="flex gap-2">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkStatusUpdate(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="px-3 py-1 text-sm border border-blue-300 rounded-md bg-white"
              >
                <option value="">Change Status...</option>
                <option value="active">Active</option>
                <option value="notice">Notice</option>
                <option value="exited">Exited</option>
              </select>
              <Button
                onClick={handleBulkDelete}
                size="sm"
                variant="destructive"
                className="flex items-center gap-1"
              >
                <Trash2 size={14} />
                Delete Selected
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Select All Checkbox */}
      {tenants.length > 0 && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selectedTenants.length === tenants.length}
            onChange={toggleSelectAll}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label className="text-sm text-gray-600">Select All ({tenants.length})</label>
        </div>
      )}

      {/* Tenants — Dense table */}
      {tenants.length === 0 ? (
        <Card className="p-12 text-center" data-testid="tenants-empty">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Tenants Yet</h3>
          {hasRooms ? (
            <>
              <p className="text-gray-600 mb-4">Click <strong>Add Tenant</strong> above to onboard your first tenant.</p>
              <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus size={16} className="mr-2" /> Add Tenant
              </Button>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-4">Tenants are assigned to rooms. Start by adding a property and at least one room.</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => window.location.href = '/manager/property/list'} className="bg-blue-600 hover:bg-blue-700">
                  <Plus size={16} className="mr-2" /> Add Property
                </Button>
                <Button onClick={() => window.location.href = '/manager/property/rooms'} variant="outline">
                  <Plus size={16} className="mr-2" /> Go to Rooms
                </Button>
              </div>
            </>
          )}
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="tenants-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase text-gray-600 tracking-wider">
                <tr>
                  <th className="p-3 w-8"></th>
                  <th className="p-3 text-left font-semibold">Tenant</th>
                  <th className="p-3 text-left font-semibold">Room / Property</th>
                  <th className="p-3 text-right font-semibold">Rent / Deposit</th>
                  <th className="p-3 text-right font-semibold">Dues</th>
                  <th className="p-3 text-center font-semibold">AI Risk</th>
                  <th className="p-3 text-center font-semibold">KYC</th>
                  <th className="p-3 text-left font-semibold">Stay</th>
                  <th className="p-3 text-center font-semibold">Status</th>
                  <th className="p-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => {
                  const isDue = ['pending', 'overdue', 'due'].includes(tenant.payment_status);
                  const dueAmount = isDue ? (tenant.rent_amount || 0) : 0;
                  // Compute tenure from check_in_date
                  let tenureLabel = '';
                  if (tenant.check_in_date) {
                    const d = new Date(tenant.check_in_date);
                    const diffMs = Date.now() - d.getTime();
                    const months = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30)));
                    tenureLabel = months >= 12 ? `${Math.floor(months / 12)}y ${months % 12}m` : `${months}m`;
                  }
                  const kycStyle =
                    tenant.kyc_status === 'verified' ? 'bg-green-100 text-green-800' :
                    tenant.kyc_status === 'pending'  ? 'bg-yellow-100 text-yellow-800' :
                    tenant.kyc_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                       'bg-gray-100 text-gray-700';
                  const statusStyle =
                    tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                    tenant.status === 'notice' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-700';
                  return (
                    <tr
                      key={tenant.id}
                      data-testid={`tenant-row-${tenant.id}`}
                      className={`border-b border-gray-100 hover:bg-blue-50/40 transition-colors ${isDue ? 'bg-red-50/20' : ''}`}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedTenants.includes(tenant.id)}
                          onChange={() => toggleSelectTenant(tenant.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => navigate(`/manager/tenants/${tenant.id}/profile`)}
                          className="flex items-center gap-3 text-left group"
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">
                            {tenant.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 group-hover:text-blue-700 flex items-center gap-1.5 flex-wrap" data-testid={`tenant-name-${tenant.id}`}>
                              {tenant.name}
                              {tenant.room_number && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 tracking-wide">
                                  Room {tenant.room_number}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Phone size={10} /> {tenant.phone}
                              {tenant.email && <><span className="mx-1">•</span><Mail size={10} /> {tenant.email}</>}
                            </p>
                          </div>
                        </button>
                      </td>
                      <td className="p-3">
                        <p className="font-medium text-gray-900">
                          {tenant.room_number ? `Room ${tenant.room_number}` : 'No room'}
                        </p>
                        <p className="text-xs text-gray-500">{tenant.property_name || '—'}</p>
                        {tenant.roommates?.length > 0 && (
                          <p className="text-[10px] text-blue-600 mt-0.5">
                            +{tenant.roommates.length} roommate{tenant.roommates.length > 1 ? 's' : ''}
                          </p>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <p className="font-semibold text-gray-900">₹{(tenant.rent_amount || 0).toLocaleString()}<span className="text-[10px] text-gray-400 font-normal">/mo</span></p>
                        <p className="text-[10px] text-gray-500">Dep ₹{(tenant.deposit_amount || 0).toLocaleString()}</p>
                      </td>
                      <td className="p-3 text-right">
                        {dueAmount > 0 ? (
                          <span className="inline-flex items-center gap-1 font-bold text-red-700">
                            ₹{dueAmount.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-xs text-green-700 inline-flex items-center gap-1">
                            <CheckCircle size={12} /> Paid
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {riskMap[tenant.id] ? (
                          (() => {
                            const r = riskMap[tenant.id];
                            const score = r.risk_score ?? 0;
                            const label = (r.risk_label || (score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low')).toLowerCase();
                            const cls = label === 'high' ? 'bg-red-100 text-red-800' : label === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800';
                            return (
                              <span
                                data-testid={`tenant-risk-chip-${tenant.id}`}
                                title={r.recommended_action || r.reasoning || ''}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}
                              >
                                <ShieldAlert size={10} /> {score} · {label}
                              </span>
                            );
                          })()
                        ) : (
                          <button
                            data-testid={`tenant-risk-${tenant.id}`}
                            onClick={() => predictPaymentRisk(tenant)}
                            disabled={riskLoading === tenant.id}
                            className="p-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded hover:opacity-90 disabled:opacity-50"
                            title="Predict Payment Risk"
                          >
                            {riskLoading === tenant.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                          </button>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${kycStyle}`}>
                          {tenant.kyc_status === 'verified' ? '✓ Verified' : (tenant.kyc_status || 'none')}
                        </span>
                        {tenant.kyc_type && (
                          <p className="text-[10px] text-gray-500 mt-0.5 capitalize">{tenant.kyc_type.replace('_', ' ')}</p>
                        )}
                      </td>
                      <td className="p-3">
                        <p className="text-xs text-gray-700">{tenant.check_in_date}</p>
                        {tenureLabel && <p className="text-[10px] text-gray-500">{tenureLabel} tenure</p>}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${statusStyle}`}>
                          {tenant.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => navigate(`/manager/tenants/${tenant.id}/profile`)}
                            data-testid={`tenant-profile-${tenant.id}`}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                            title="View Profile"
                          >
                            <User size={14} />
                          </button>
                          <button
                            onClick={() => handleEditTenant(tenant)}
                            data-testid={`tenant-edit-${tenant.id}`}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteTenant(tenant)}
                            data-testid={`tenant-delete-${tenant.id}`}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                          {tenant.kyc_status === 'pending' && (
                            <button
                              onClick={() => updateKYCStatus(tenant.id, 'verified')}
                              data-testid={`tenant-kyc-approve-${tenant.id}`}
                              className="px-2 py-1 text-[10px] bg-green-600 text-white rounded hover:bg-green-700"
                              title="Approve KYC"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
</div>
);
};

export default TenantsList;
